import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CalendarDays, Tag, Store, MapPin, ArrowRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { Ltr } from "@/lib/bidi";
import { SeoPage } from "@/components/seo-page";

type Deal = {
  id: number; title: string; description: string; dealType: string; regularPriceAed: number | null;
  dealPriceAed: number | null; savingAed: number | null; savingPercent: number | null;
  startsOn: string; expiresOn: string; conditions: string; merchantName: string; merchantArea: string;
  listingId: number | null; listingTitle: string | null; isFeatured: boolean; isSponsored: boolean;
};

async function getDeals(): Promise<Deal[]> {
  const response = await fetch("/api/deals");
  if (!response.ok) throw new Error("Unable to load deals");
  return response.json();
}

export default function DealsPage() {
  const { language, isRTL } = useI18n();
  const { data: deals = [], isLoading, isError } = useQuery({ queryKey: ["/api/deals"], queryFn: getDeals });
  const copy = language === "ar"
    ? { title: "عروض دبي", subtitle: "عروض حقيقية من تجار مستقلين في دبي. تواصل مع التاجر مباشرة لتأكيد التوفر وشروط الدفع.", empty: "لا توجد عروض معتمدة حالياً.", conditions: "الشروط", expires: "ينتهي", request: "اطلب من التاجر", sponsored: "برعاية", featured: "مميز", loading: "جارٍ تحميل العروض…", error: "تعذر تحميل العروض. يرجى المحاولة مرة أخرى.", save: "وفّر" }
    : { title: "Dubai Deals", subtitle: "Genuine offers from independent Dubai merchants. Contact each merchant directly to confirm availability and payment terms.", empty: "No approved live deals are available right now.", conditions: "Conditions", expires: "Ends", request: "Request from merchant", sponsored: "Sponsored", featured: "Featured", loading: "Loading deals…", error: "Deals could not be loaded. Please try again.", save: "Save" };
  return (
    <div className="flex-1 bg-muted/10 pb-20">
      <SeoPage page={{ title: language === "ar" ? "عروض USDT في دبي | Souq Crypto" : "Dubai USDT Deals | Souq Crypto", description: copy.subtitle, path: "/deals" }} />
      <div className="container mx-auto max-w-6xl px-4 py-12 md:py-16">
        <header className="mb-12 max-w-3xl">
          <Badge className="mb-4 rounded-full bg-primary/10 px-4 py-1.5 text-sm text-primary border border-primary/20 shadow-sm">
            <Tag className="mr-2 h-4 w-4" />
            <Ltr className="font-semibold uppercase tracking-wider">Souq Crypto</Ltr>
          </Badge>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-[#062A60] dark:text-white">{copy.title}</h1>
          <p className="text-lg text-muted-foreground">{copy.subtitle}</p>
        </header>

        {isLoading ? <div className="py-20 text-center flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> : null}
        
        {isError ? (
          <div className="rounded-2xl border border-destructive/20 bg-card p-10 text-center shadow-sm">
            <p className="text-lg font-medium text-destructive">{copy.error}</p>
          </div>
        ) : null}
        
        {!isLoading && !isError && deals.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-border/60 bg-card/50 p-16 text-center">
            <Tag className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">{copy.empty}</h2>
          </div>
        ) : null}
        
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {deals.map((deal) => (
            <Card key={deal.id} className="overflow-hidden rounded-2xl border-border/60 hover:border-primary/50 hover:shadow-xl transition-all duration-300 flex flex-col h-full group">
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-muted border-b border-border/50">
                <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                  <Tag className="h-20 w-20 text-primary/20 mb-4 group-hover:scale-110 group-hover:text-primary/30 transition-all duration-500" />
                </div>
                
                <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                  {deal.isFeatured && <Badge className="bg-accent/90 border-accent-foreground/10 text-accent-foreground backdrop-blur-sm shadow-sm font-semibold">{copy.featured}</Badge>}
                  {deal.isSponsored && <Badge variant="secondary" className="bg-background/80 backdrop-blur-md shadow-sm font-medium">{copy.sponsored}</Badge>}
                  {deal.savingPercent ? <Badge className="bg-primary/95 hover:bg-primary text-primary-foreground backdrop-blur-sm shadow-sm font-bold text-sm px-3 py-1">{copy.save} <Ltr>{deal.savingPercent}%</Ltr></Badge> : null}
                </div>
              </div>
              
              <div className="flex-1 flex flex-col p-6">
                <div className="mb-2">
                  <h2 className="font-bold text-xl line-clamp-2 group-hover:text-primary transition-colors">{deal.title}</h2>
                </div>
                
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4 font-medium">
                  <Store className="h-3.5 w-3.5" />
                  <span className="truncate">{deal.merchantName}</span>
                  <span>•</span>
                  <MapPin className="h-3.5 w-3.5" />
                  <span className="truncate">{deal.merchantArea}</span>
                </div>
                
                <p className="text-sm leading-relaxed line-clamp-2 text-muted-foreground flex-1 mb-5">{deal.description}</p>
                
                {deal.dealPriceAed != null && (
                  <div className="flex items-baseline gap-2 mb-4">
                    <Ltr className="text-2xl font-extrabold text-[#062A60] dark:text-white">AED {deal.dealPriceAed.toLocaleString()}</Ltr>
                    {deal.regularPriceAed != null && <Ltr className="text-sm text-muted-foreground line-through font-medium">AED {deal.regularPriceAed.toLocaleString()}</Ltr>}
                  </div>
                )}
                
                <div className="rounded-xl bg-muted/50 border border-border/40 p-4 text-xs text-muted-foreground mb-5 space-y-2">
                  <div className="flex gap-2">
                    <span className="font-bold text-foreground shrink-0">{copy.conditions}:</span>
                    <span className="line-clamp-2 leading-relaxed">{deal.conditions}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-primary font-semibold pt-2 border-t border-border/50">
                    <CalendarDays className="h-4 w-4" /> 
                    <span>{copy.expires} {deal.expiresOn}</span>
                  </div>
                </div>
                
                {deal.listingId && (
                  <div className="mt-auto">
                    <Button className="w-full h-11 font-bold group-hover:bg-primary group-hover:text-primary-foreground shadow-md transition-all" asChild>
                      <Link
                        href={`/listings/${deal.listingId}`}
                        onClick={() => import("@/lib/analytics").then(m => m.trackDealViewed({})).catch(() => {})}
                      >
                        {copy.request}
                        <ArrowRight className={`h-4 w-4 ${isRTL ? "mr-2 rtl-flip" : "ml-2"}`} />
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
